import type {
  BlockReleaseKind,
  BlockTargetOption,
  CompleteStageResult,
  DeliverableTypeOption,
  Inbox,
  DeliverableView,
  AttentionSummary,
  ManagedCalendar,
  NeedsAttention,
  MyCalendar,
  MyWork,
  FinishedWork,
  Overview,
  Workload,
  MentionView,
  NoteImageType,
  NoteView,
  DateChangeReason,
  CreateProjectInput,
  CreateTaskInput,
  ProjectType,
  ProjectView,
  ReportCategory,
  CreateReportInput,
  ReportView,
  SchedulerReports,
  ReferenceOption,
  TaskDetail,
  TaskView,
  DeploymentOption,
  UpdateTaskInput,
  DeliverableEditability,
  DateChangeEntry,
  BlockOptions,
  SettingView,
  TemplateUsage,
  TemplateCatalog,
  AddFromTemplatesResult,
  ProjectGrid,
  ItemCatalog,
  FieldSubject,
  FieldValue,
  FieldView,
  FieldAdminView,
  FieldAudience,
  FieldScopeChoices,
  FieldScopeInput,
  NewFieldInput,
  NewPunchItemInput,
  PunchHistoryEntry,
  PunchItem,
  PunchItemInput,
  PunchListView,
} from "@fieldpath/shared";

/**
 * Typed wrapper over the API.
 *
 * Every call goes through the authenticated fetch from useApi, which attaches a
 * token. Errors carry the API's message rather than a status code, because the
 * API returns messages a person can act on — "Client and Proposal projects
 * require a client" beats "400".
 */
export type Fetcher = (path: string, init?: RequestInit) => Promise<Response>;

export class ApiError extends Error {
  /**
   * The HTTP status, so a caller can tell a refusal from a failure. A 403 on a
   * PM-only screen is something to explain, not an error to show.
   */
  constructor(
    message: string,
    readonly status: number,
    /**
     * The API's request id, present on 500s only. Carried separately as well as
     * being in the message, so a "report this" control can send the bare id
     * rather than asking somebody to retype it out of a sentence.
     */
    readonly reference?: string,
  ) {
    super(message);
  }
}

/** Survives a page change; private windows throw, and that is not fatal. */
function rememberReference(reference: string): void {
  try {
    window.sessionStorage.setItem("fieldpath:lastErrorReference", reference);
  } catch {
    // Blocked site data. A report without a reference is still a report.
  }
}

/**
 * The headers a call sends: `Content-Type: application/json` ONLY when there is
 * a body (punch list F186, 2 Nov 2026). Sent on a DELETE with no body, Fastify
 * refuses it as an empty JSON body (FST_ERR_CTP_EMPTY_JSON_BODY) -- every
 * remove in the app (a figure or checklist item, unlinking a booking, a field's
 * scope) failed that way from the day each was built.
 */
export function headersFor(init?: RequestInit): HeadersInit {
  return init?.body === undefined || init.body === null
    ? { ...init?.headers }
    : { "Content-Type": "application/json", ...init.headers };
}

async function call<T>(fetcher: Fetcher, path: string, init?: RequestInit): Promise<T> {
  const response = await fetcher(path, { ...init, headers: headersFor(init) });

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => null)) as
    | { error?: string; reference?: string }
    | null;

  if (!response.ok) {
    // Remember the reference so "Report a problem" can attach it without the
    // person copying anything. Stored rather than held in state because the
    // error and the report happen in different components, often minutes apart.
    if (body?.reference) rememberReference(body.reference);
    throw new ApiError(
      body?.error ?? `Request failed (${response.status})`,
      response.status,
      body?.reference,
    );
  }
  return body as T;
}

const json = (value: unknown): RequestInit => ({
  method: "POST",
  body: JSON.stringify(value),
});

export const api = {
  listProjects: (f: Fetcher) =>
    call<{ projects: ProjectView[] }>(f, "/api/projects").then((r) => r.projects),

  getProject: (f: Fetcher, id: string) =>
    call<{ project: ProjectView }>(f, `/api/projects/${id}`).then((r) => r.project),

  createProject: (f: Fetcher, input: CreateProjectInput) =>
    call<{ project: ProjectView }>(f, "/api/projects", json(input)).then((r) => r.project),

  /** Tasks and deliverables together: the project view groups one by the other. */
  listProjectWork: (f: Fetcher, projectId: string) =>
    call<{ tasks: TaskView[]; deliverables: DeliverableView[] }>(
      f,
      `/api/projects/${projectId}/tasks`,
    ),

  listTasks: (f: Fetcher, projectId: string) =>
    call<{ tasks: TaskView[] }>(f, `/api/projects/${projectId}/tasks`).then((r) => r.tasks),

  /** What the signed-in person may change on a deliverable (E5.4). */
  deliverableEditability: (f: Fetcher, id: string) =>
    call<{ editability: DeliverableEditability }>(f, `/api/deliverables/${id}`).then(
      (r) => r.editability,
    ),

  /** Why its client date moved, newest first (E5.4). */
  deliverableDateChanges: (f: Fetcher, id: string) =>
    call<{ dateChanges: DateChangeEntry[] }>(f, `/api/deliverables/${id}/date-changes`).then(
      (r) => r.dateChanges,
    ),

  getDeliverable: (f: Fetcher, id: string) =>
    call<{ deliverable: DeliverableView }>(f, `/api/deliverables/${id}`).then(
      (r) => r.deliverable,
    ),

  figureTypes: (f: Fetcher) =>
    call<{ types: { id: string; name: string }[] }>(f, "/api/figure-types").then(
      (r) => r.types,
    ),

  states: (f: Fetcher) =>
    call<{ states: { code: string; name: string }[] }>(f, "/api/states").then(
      (r) => r.states,
    ),

  deliverableTypes: (f: Fetcher) =>
    call<{ types: DeliverableTypeOption[] }>(f, "/api/deliverable-types").then(
      (r) => r.types,
    ),

  createDeliverable: (
    f: Fetcher,
    projectId: string,
    input: {
      name: string;
      typeId?: string | null;
      clientDueDate?: string | null;
      ownerKey?: string | null;
    },
  ) =>
    call<{ deliverable: DeliverableView }>(
      f,
      `/api/projects/${projectId}/deliverables`,
      json(input),
    ).then((r) => r.deliverable),

  updateDeliverable: (
    f: Fetcher,
    id: string,
    patch: {
      name?: string;
      typeId?: string | null;
      clientDueDate?: string | null;
      ownerKey?: string | null;
      gatingTaskId?: string | null;
      /** Required when the client date moves LATER (E5.4). */
      reasonCategory?: DateChangeReason;
      reasonNote?: string | null;
    },
  ) =>
    call<{ deliverable: DeliverableView }>(f, `/api/deliverables/${id}`, {
      ...json(patch),
      method: "PATCH",
    }).then((r) => r.deliverable),

  /**
   * Correct a project after the fact. The API has always allowed this and
   * audits every field change; there was simply no way to ask for it.
   */
  updateProject: (
    f: Fetcher,
    id: string,
    patch: {
      name?: string;
      projectType?: ProjectType;
      clientKey?: string | null;
      deltekProjectKey?: string | null;
      pmKey?: string | null;
      tmKey?: string | null;
    },
  ) =>
    call<{ project: ProjectView }>(f, `/api/projects/${id}`, {
      ...json(patch),
      method: "PATCH",
    }).then((r) => r.project),

  setProjectCancelled: (f: Fetcher, id: string, cancelled: boolean) =>
    call<{ project: ProjectView }>(
      f,
      `/api/projects/${id}/cancelled`,
      json({ cancelled }),
    ).then((r) => r.project),

  setDeliverableCancelled: (f: Fetcher, id: string, cancelled: boolean) =>
    call<{ deliverable: DeliverableView }>(
      f,
      `/api/deliverables/${id}/cancelled`,
      json({ cancelled }),
    ).then((r) => r.deliverable),

  setProjectCompleted: (f: Fetcher, id: string, completed: boolean) =>
    call<{ project: ProjectView }>(
      f,
      `/api/projects/${id}/completed`,
      json({ completed }),
    ).then((r) => r.project),

  setDeliverableCompleted: (f: Fetcher, id: string, completed: boolean) =>
    call<{ deliverable: DeliverableView }>(
      f,
      `/api/deliverables/${id}/completed`,
      json({ completed }),
    ).then((r) => r.deliverable),

  deleteDeliverable: (f: Fetcher, id: string) =>
    call<void>(f, `/api/deliverables/${id}`, { method: "DELETE" }),

  updateTask: (f: Fetcher, id: string, patch: UpdateTaskInput) =>
    call<{ task: TaskView }>(f, `/api/tasks/${id}`, {
      ...json(patch),
      method: "PATCH",
    }).then((r) => r.task),

  // A Field Mobilization task's resource-scheduler booking (21 Oct 2026; db/deployment-sync.ts).
  /** The project's live bookings no other task has, or why they could not be read. */
  deploymentOptions: (f: Fetcher, taskId: string) =>
    call<{ available: true; options: DeploymentOption[] } | { available: false; reason: string }>(
      f, `/api/tasks/${taskId}/deployment-options`),

  /** Link one; the stage is filled from it at once. */
  linkDeployment: (f: Fetcher, taskId: string, deploymentId: string) =>
    call<{ unknownCrew: string[] }>(f, `/api/tasks/${taskId}/deployment`, { ...json({ deploymentId }), method: "PUT" }),

  /** Unlink: the stage keeps what it had and is editable again. */
  unlinkDeployment: (f: Fetcher, taskId: string) =>
    call<void>(f, `/api/tasks/${taskId}/deployment`, { method: "DELETE" }),

  /**
   * Bring the task up to date with its booking now. Never ends a link because the scheduler is
   * down. `json({})`, like every body-less POST here: `call` always says the body is JSON, and
   * Fastify refuses an EMPTY JSON body with a 400 -- which every refresh from the task page hit
   * until 21 Oct 2026, silently, while only the 15-minute sync kept tasks up to date.
   */
  refreshDeployment: (f: Fetcher, taskId: string) =>
    call<{ available: boolean; reason?: string; refreshed: number; lost: number; unknownCrew: string[] }>(
      f, `/api/tasks/${taskId}/deployment/refresh`, json({})),

  /** The task a deployment is linked to, for the scheduler's link back; null when none. */
  taskForMobilization: (f: Fetcher, deploymentId: string) =>
    call<{ taskId: string | null }>(f, `/api/mobilizations/${deploymentId}`).then((r) => r.taskId),

  createTask: (
    f: Fetcher,
    projectId: string,
    input: Omit<CreateTaskInput, "projectId"> & {
      roleAssignments?: Record<string, string[]>;
    },
  ) =>
    call<{ task: TaskView }>(f, `/api/projects/${projectId}/tasks`, json(input)).then(
      (r) => r.task,
    ),

  /**
   * Cancel a task, or put it back.
   *
   * The reason is sent only when cancelling. Reinstating genuinely has none,
   * and sending an empty string would be a different claim from sending
   * nothing -- the same blank-versus-absent trap that put `""` into a report
   * reference column on 2 Oct.
   */
  setTaskCancelled: (f: Fetcher, id: string, cancelled: boolean, reason?: string) =>
    call<{ task: TaskView }>(
      f,
      `/api/tasks/${id}/cancelled`,
      json(cancelled ? { cancelled, reason } : { cancelled }),
    ).then((r) => r.task),

  /**
   * Requested work (punch list F140): approve, naming its PM / Task Manager
   * (left out: the project's PM), or reject with the cancel's reason.
   */
  approveRequest: (f: Fetcher, kind: "tasks" | "deliverables", id: string, ownerKey?: string | null) =>
    call<unknown>(f, `/api/${kind}/${id}/request/approve`, json(ownerKey === undefined ? {} : { ownerKey })),
  rejectRequest: (f: Fetcher, kind: "tasks" | "deliverables", id: string, reason: string) =>
    call<unknown>(f, `/api/${kind}/${id}/request/reject`, json({ reason })),

  getTask: (f: Fetcher, taskId: string) =>
    call<{ task: TaskView }>(f, `/api/tasks/${taskId}`).then((r) => r.task),

  getTaskWorkflow: (f: Fetcher, taskId: string) =>
    call<TaskDetail>(f, `/api/tasks/${taskId}/workflow`),

  startStage: (f: Fetcher, taskId: string) =>
    call<void>(f, `/api/tasks/${taskId}/stage/start`, json({})),

  /** The current stage back to Not started, while it has not moved on (F139/F142). */
  undoStartStage: (f: Fetcher, taskId: string) =>
    call<void>(f, `/api/tasks/${taskId}/stage/undo-start`, json({})),

  /**
   * `hibernate` blocks it AND stops surfacing it — a report the client has
   * gone quiet on, rather than one somebody should be chasing. It is only
   * ever sent when blocking; releasing clears it, because hibernation lives
   * on the block and there is no hibernation without one.
   */
  setBlocked: (
    f: Fetcher,
    taskId: string,
    blocked: boolean,
    /**
     * An OPTIONS OBJECT, not more positional arguments. It grew a third
     * condition on 10 Oct 2026 and `setBlocked(f, id, true, r, h, k, w, s)`
     * is a call nobody can read or get right — the two nullable ids next to
     * each other being the sharp part.
     *
     * It is also the shape the server-side setter already takes.
     */
    opts: {
      reason?: string;
      hibernate?: boolean;
      releaseKind?: BlockReleaseKind;
      waitsOnStageId?: string | null;
      snoozeUntil?: string | null;
      /** A named reason (hold decision 1); `reason` is then the optional detail. */
      reasonKey?: string | null;
    } = {},
  ) => call<void>(f, `/api/tasks/${taskId}/stage/blocked`, json({ blocked, ...opts })),

  /** What the Block dialog offers on the current stage: reasons, and where its snoozes stand. */
  getBlockOptions: (f: Fetcher, taskId: string) =>
    call<{ options: BlockOptions }>(f, `/api/tasks/${taskId}/stage/block-options`).then(
      (r) => r.options,
    ),

  /**
   * What this task may be offered as things to wait on.
   *
   * Fetched when the Block dialog OPENS, not with the task, because it is
   * only ever needed there and it grows with the project.
   */
  getBlockTargets: (f: Fetcher, taskId: string) =>
    call<BlockTargetOption[]>(f, `/api/tasks/${taskId}/stage/block-targets`),

  /**
   * Point the current stage's UNRESOLVED block at a real stage, re-point it,
   * or unlink it with `null`. It stays blocked throughout -- unlinking is not
   * releasing.
   */
  setBlockTarget: (f: Fetcher, taskId: string, waitsOnStageId: string | null) =>
    call<void>(f, `/api/tasks/${taskId}/stage/block-target`, {
      ...json({ waitsOnStageId }),
      method: "PUT",
    }),

  completeStage: (
    f: Fetcher,
    taskId: string,
    outcome?: string,
    reason?: string,
    /** Required when the PM advances a stage that is not theirs. */
    pmNote?: string,
    /** Move into an unready stage anyway (E7.9); pmNote says why. */
    overrideUnready?: boolean,
    /** Warned the next stage's date has passed, and moving on (E11.5). */
    acknowledgePastDue?: boolean,
  ) =>
    // TYPED, not `unknown`. The route has returned `{ result }` all along;
    // the browser just could not see inside it, which is why the person who
    // advanced could not be told what else their click did.
    call<{ result: CompleteStageResult }>(
      f,
      `/api/tasks/${taskId}/stage/complete`,
      json({
        ...(outcome ? { outcome } : {}),
        ...(reason ? { reason } : {}),
        ...(pmNote ? { pmNote } : {}),
        ...(overrideUnready ? { overrideUnready: true } : {}),
        ...(acknowledgePastDue ? { acknowledgePastDue: true } : {}),
      }),
    ),

  updateStage: (
    f: Fetcher,
    taskId: string,
    stageId: string,
    body: {
      allocatedHours?: number | null;
      description?: string | null;
      dueDate?: string | null;
      assigneeKeys?: string[];
      reasonCategory?: DateChangeReason;
      reasonNote?: string | null;
    },
  ) =>
    call<void>(f, `/api/tasks/${taskId}/stages/${stageId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** The same save from Plan all stages: PM-flagged people and admins only (F121). */
  updatePlannedStage: (
    f: Fetcher,
    taskId: string,
    stageId: string,
    body: { allocatedHours?: number | null; dueDate?: string | null; assigneeKeys?: string[] },
  ) =>
    call<void>(f, `/api/tasks/${taskId}/plan/stages/${stageId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** An admin moves the task back a stage, undoing a misclick (E5a). */
  reverseStage: (f: Fetcher, taskId: string, note: string) =>
    call<{ result: { from: string; to: string } }>(f, `/api/tasks/${taskId}/stage/reverse`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),

  /** A stage holder asks for a date to move (E5.2). Null stage: the task's own date. */
  requestDateChange: (
    f: Fetcher,
    taskId: string,
    body: {
      taskStageId: string | null;
      date: string | null;
      reasonCategory?: DateChangeReason;
      note?: string | null;
    },
  ) =>
    call<{ id: string }>(f, `/api/tasks/${taskId}/date-requests`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** The task's PM or an admin approves or rejects (E5.3). */
  decideDateChange: (f: Fetcher, requestId: string, approve: boolean, note?: string | null) =>
    call<void>(f, `/api/date-requests/${requestId}/decision`, {
      method: "POST",
      body: JSON.stringify({ approve, ...(note ? { note } : {}) }),
    }),

  updateTaskDueDate: (
    f: Fetcher,
    taskId: string,
    dueDate: string | null,
    reasonCategory?: DateChangeReason,
    reasonNote?: string | null,
  ) =>
    call<void>(f, `/api/tasks/${taskId}/due-date`, {
      method: "PATCH",
      body: JSON.stringify({ dueDate, reasonCategory, reasonNote }),
    }),

  employees: (f: Fetcher) =>
    call<{ employees: ReferenceOption[] }>(f, "/api/reference/employees").then(
      (r) => r.employees,
    ),

  workflowRoles: (f: Fetcher, workflowId: string) =>
    call<{ roles: { id: string; name: string; stages: { name: string; first: boolean }[] }[] }>(
      f,
      `/api/workflows/${workflowId}/roles`,
    ).then((r) => r.roles),

  myWork: (f: Fetcher) => call<MyWork>(f, "/api/my/work"),

  /** New personal task (F171): only the signed-in person will see it. */
  createPersonalTask: (f: Fetcher, input: { title: string; dueDate: string | null }) =>
    call<{ task: TaskView }>(f, "/api/my/personal-tasks", json(input)).then((r) => r.task),

  /** Needs attention (A.1): problems and concerns across the work I manage. */
  needsAttention: (f: Fetcher, horizon: number, scope: "mine" | "all") =>
    call<NeedsAttention>(f, `/api/attention?horizon=${horizon}&scope=${scope}`),

  /** The nav flag beside Needs attention. */
  attentionSummary: (f: Fetcher) => call<AttentionSummary>(f, "/api/attention/summary"),

  /** My calendar (C.1): my stages, HR's holidays and my time off, over a month view. */
  myCalendar: (f: Fetcher, from: string, through: string) =>
    call<MyCalendar>(f, `/api/my/calendar?from=${from}&through=${through}`),

  /** The calendar of the work I manage (C.1). */
  managedCalendar: (f: Fetcher, from: string, through: string) =>
    call<ManagedCalendar>(f, `/api/my/managed-calendar?from=${from}&through=${through}`),

  /**
   * Finished work (punch list F117): my finished stages between two of MY calendar days, so the
   * browser's offset from UTC goes with them.
   */
  myFinished: (f: Fetcher, from: string, through: string, search: string) =>
    call<FinishedWork>(
      f,
      `/api/my/finished?from=${from}&through=${through}&tz=${new Date().getTimezoneOffset()}` +
        (search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ""),
    ),

  workload: (f: Fetcher, days: number) =>
    call<Workload>(f, `/api/workload?days=${days}`),

  /** How far N working days from today reaches (punch list #13). */
  businessWindow: (f: Fetcher, workingDays: number) =>
    call<{ from: string; through: string; calendarDays: number; holidaysKnown: boolean }>(
      f,
      `/api/business-window?days=${workingDays}`,
    ),

  /** PM only. The server refuses with 403; the screen explains rather than erroring. */
  overview: (f: Fetcher) => call<Overview>(f, "/api/overview"),

  /**
   * Who signed in, and the Deltek record behind it: null for somebody with
   * none, who gets the no-access screen and nothing else (linked-guard.ts).
   */
  me: (f: Fetcher) =>
    call<{
      user: { oid: string; name: string | null; email: string | null };
      employee: { ajeraKey: string; name: string } | null;
    }>(f, "/api/me"),

  listNotes: (f: Fetcher, taskId: string) =>
    call<{ notes: NoteView[] }>(f, `/api/tasks/${taskId}/notes`).then((r) => r.notes),

  /** With its images, as base64, in the same post (3 Nov 2026). */
  addNote: (
    f: Fetcher,
    taskId: string,
    body: string,
    mentionKeys: string[],
    images: readonly { contentType: NoteImageType; data: string }[] = [],
  ) =>
    call<{ notes: NoteView[] }>(f, `/api/tasks/${taskId}/notes`, {
      method: "POST",
      body: JSON.stringify({ body, mentionKeys, images }),
    }).then((r) => r.notes),

  /**
   * One image of a note, as a Blob. Fetched with the sign-in token, since an
   * <img> cannot send one; the page shows it from an object URL.
   */
  noteImage: async (f: Fetcher, taskId: string, imageId: string): Promise<Blob> => {
    const response = await f(`/api/tasks/${taskId}/note-images/${imageId}`);
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new ApiError(body?.error ?? `Request failed (${response.status})`, response.status);
    }
    return response.blob();
  },

  inbox: (f: Fetcher) => call<Inbox>(f, "/api/inbox"),

  inboxUnread: (f: Fetcher) =>
    call<{ unread: number }>(f, "/api/inbox/unread"),

  markInboxRead: (f: Fetcher, ids?: string[]) =>
    call<{ marked: number; unread: number }>(
      f,
      "/api/inbox/read",
      json(ids ? { ids } : {}),
    ),

  myMentions: (f: Fetcher) =>
    call<{ mentions: MentionView[] }>(f, "/api/me/mentions"),

  markMentionsRead: (f: Fetcher, noteIds: string[]) =>
    call<void>(f, "/api/me/mentions/read", {
      method: "POST",
      body: JSON.stringify({ noteIds }),
    }),

  listWorkflows: (f: Fetcher) =>
    call<{
      workflows: { id: string; name: string; requiresFigureType: boolean }[];
    }>(f, "/api/workflows").then((r) => r.workflows),

  clients: (f: Fetcher) =>
    call<{ clients: ReferenceOption[] }>(f, "/api/reference/clients").then((r) => r.clients),

  projectManagers: (f: Fetcher) =>
    call<{ projectManagers: ReferenceOption[] }>(f, "/api/reference/project-managers").then(
      (r) => r.projectManagers,
    ),

  reportCategories: (f: Fetcher) =>
    call<{ categories: ReportCategory[] }>(f, "/api/report-categories").then(
      (r) => r.categories,
    ),

  reportProblem: (f: Fetcher, input: CreateReportInput) =>
    call<{ report: ReportView }>(f, "/api/reports", json(input)).then((r) => r.report),

  deltekProjects: (f: Fetcher, clientKey: string) =>
    call<{ deltekProjects: ReferenceOption[] }>(
      f,
      `/api/reference/deltek-projects?clientKey=${encodeURIComponent(clientKey)}`,
    ).then((r) => r.deltekProjects),

  /*
   * Admin. Every one of these is refused with 403 for anybody whose Deltek
   * record does not carry the admin flag -- the check is on the server, and
   * these calls exist on the client for everybody.
   */
  amIAdmin: (f: Fetcher) =>
    call<{ isAdmin: boolean }>(f, "/api/admin/me")
      .then((r) => r.isAdmin)
      // A 403 is the ordinary answer for most people, not a failure worth
      // surfacing. Anything else is a real problem and still throws.
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) return false;
        throw e;
      }),

  ivpReport: (f: Fetcher) =>
    call<{ report: IvpReport | null }>(f, "/api/admin/ivp").then((r) => r.report),

  deltekStatus: (f: Fetcher) =>
    call<{ status: DeltekStatus; executions: DeltekExecution[] }>(f, "/api/admin/deltek"),

  /**
   * The work data wipe -- the training reset and the go-live wipe (E10.3; Jeremy,
   * 22 Oct 2026): whether it is switched on, and the rows in each work table now.
   */
  goLiveWipe: (f: Fetcher) =>
    call<{ enabled: boolean; counts?: Record<string, number> }>(f, "/api/admin/go-live-wipe"),

  runGoLiveWipe: (f: Fetcher, confirm: string) =>
    call<{ before: Record<string, number>; after: Record<string, number> }>(
      f,
      "/api/admin/go-live-wipe",
      json({ confirm }),
    ),

  /** The one-time Wrike import (1 Nov 2026): whether it is switched on, and a press. */
  wrikeImport: (f: Fetcher) => call<{ enabled: boolean }>(f, "/api/admin/wrike-import"),

  runWrikeStages: (f: Fetcher, confirm: string) =>
    call<{ stagesUpdated: number; peopleAdded: number; datesSet: number; unchanged: number; problems: string[] }>(
      f,
      "/api/admin/wrike-stages",
      json({ confirm }),
    ),

  runWrikeImport: (f: Fetcher, confirm: string) =>
    call<{
      projectsCreated: number;
      projectsSkipped: number;
      skippedProjects: string[];
      deliverables: number;
      tasks: number;
      problems: string[];
    }>(f, "/api/admin/wrike-import", json({ confirm })),

  /** Custom fields (E3/4): what this person may see on one subject, and a change. */
  fields: (f: Fetcher, subject: FieldSubject) =>
    call<{ fields: FieldView[] }>(f, `/api/fields/${subject.level}/${subject.id}`).then(
      (r) => r.fields,
    ),

  setField: (f: Fetcher, subject: FieldSubject, fieldId: string, value: FieldValue) =>
    call<void>(f, `/api/fields/${subject.level}/${subject.id}/${fieldId}`, {
      ...json({ value }),
      method: "PUT",
    }),

  /** The stage object listing (E1): add, remove an unticked item, tick. */
  addStageItem: (f: Fetcher, taskStageId: string, input: { typeId?: string; name?: string }) =>
    call<{ item: { id: string } }>(f, `/api/task-stages/${taskStageId}/items`, json(input)),

  removeStageItem: (f: Fetcher, itemId: string) =>
    call<void>(f, `/api/stage-items/${itemId}`, { method: "DELETE" }),

  setStageItem: (
    f: Fetcher,
    itemId: string,
    input: { done: boolean; filePath?: string | null; note?: string | null },
  ) => call<void>(f, `/api/stage-items/${itemId}`, { ...json(input), method: "PATCH" }),

  /** A figure's number (F101); a number already taken swaps. */
  setStageItemNumber: (f: Fetcher, itemId: string, number: number) =>
    call<void>(f, `/api/stage-items/${itemId}/number`, { ...json({ number }), method: "PUT" }),

  /** The grid: every task of a project with what the viewer may change (E2 5b). */
  projectGrid: (f: Fetcher, projectId: string) =>
    call<{ grid: ProjectGrid }>(f, `/api/projects/${projectId}/grid`).then((r) => r.grid),

  /** Everybody in a role, on every stage it still holds (E2.7). */
  setRoleAssignees: (f: Fetcher, taskId: string, roleId: string, assigneeKeys: string[]) =>
    call<{ result: { stagesChanged: number } }>(f, `/api/tasks/${taskId}/roles/${roleId}`, {
      ...json({ assigneeKeys }),
      method: "PUT",
    }),

  /** Every template, for the Add from templates picker (E2.2). */
  templateCatalog: (f: Fetcher) =>
    call<{ catalog: TemplateCatalog }>(f, "/api/templates").then((r) => r.catalog),

  /** Templates become real work on a project, in one transaction (E2.2). */
  addFromTemplates: (
    f: Fetcher,
    projectId: string,
    body: {
      deliverableTemplateIds: string[];
      taskTemplateIds: string[];
      dueDate: string | null;
    },
  ) =>
    call<{ result: AddFromTemplatesResult }>(
      f,
      `/api/projects/${projectId}/add-from-templates`,
      json(body),
    ).then((r) => r.result),

  /** Field definitions on /admin (E3/4 row 5). */
  fieldAdmin: (f: Fetcher) =>
    call<{ fields: FieldAdminView[]; choices: FieldScopeChoices }>(f, "/api/admin/fields"),

  createField: (f: Fetcher, input: NewFieldInput) =>
    call<{ field: { id: string } }>(f, "/api/admin/fields", json(input)),

  updateField: (
    f: Fetcher,
    id: string,
    patch: {
      label?: string;
      helpText?: string | null;
      required?: boolean;
      visibleTo?: FieldAudience;
      editableBy?: FieldAudience;
      inGrid?: boolean;
      isActive?: boolean;
    },
  ) => call<void>(f, `/api/admin/fields/${id}`, { ...json(patch), method: "PATCH" }),

  addFieldOption: (f: Fetcher, fieldId: string, label: string) =>
    call<{ option: { id: string } }>(f, `/api/admin/fields/${fieldId}/options`, json({ label })),

  updateFieldOption: (f: Fetcher, optionId: string, patch: { label?: string; isActive?: boolean }) =>
    call<void>(f, `/api/admin/field-options/${optionId}`, { ...json(patch), method: "PATCH" }),

  addFieldScope: (f: Fetcher, fieldId: string, scope: FieldScopeInput) =>
    call<void>(f, `/api/admin/fields/${fieldId}/scopes`, json(scope)),

  removeFieldScope: (f: Fetcher, scopeId: string) =>
    call<void>(f, `/api/admin/field-scopes/${scopeId}`, { method: "DELETE" }),

  /** The item catalog on /admin (E1). */
  itemCatalog: (f: Fetcher) =>
    call<{ catalog: ItemCatalog }>(f, "/api/admin/item-catalog").then((r) => r.catalog),

  addItemType: (f: Fetcher, listKey: string, name: string) =>
    call<{ type: { id: string } }>(f, `/api/admin/item-catalog/${encodeURIComponent(listKey)}`, json({ name })),

  updateItemType: (f: Fetcher, id: string, patch: { name?: string; isActive?: boolean }) =>
    call<void>(f, `/api/admin/item-catalog/types/${id}`, { ...json(patch), method: "PATCH" }),

  /** The template list on /admin (E2 7a). */
  templateUsage: (f: Fetcher) =>
    call<{ usage: TemplateUsage }>(f, "/api/admin/templates").then((r) => r.usage),

  setTemplateActive: (f: Fetcher, kind: "task" | "deliverable", id: string, isActive: boolean) =>
    call<void>(f, `/api/admin/templates/${kind}/${id}`, { ...json({ isActive }), method: "PATCH" }),

  /** "Offer on its own" for a task template that a deliverable template lists. */
  setTemplateOfferAlone: (f: Fetcher, id: string, offerAlone: boolean) =>
    call<void>(f, `/api/admin/templates/task/${id}/offer-alone`, { ...json({ offerAlone }), method: "PATCH" }),

  /** The settings panel (PART 3 app 8). */
  settings: (f: Fetcher) =>
    call<{ settings: SettingView[] }>(f, "/api/admin/settings").then((r) => r.settings),

  updateSetting: (f: Fetcher, key: string, value: string) =>
    call<void>(f, `/api/admin/settings/${encodeURIComponent(key)}`, {
      ...json({ value }),
      method: "PUT",
    }),

  /** Cancel every open task, deliverable and project. The body must say RESET. */
  /** The click-through's test data, on dev only (db/test-scenarios.ts). */
  /**
   * The last 24 hours of activity in both apps, as an Excel file (the admin
   * page, 2 Nov 2026). A Blob and the server's file name, not JSON; a refusal
   * still carries the API's message, as every other call does.
   */
  downloadActivity: async (f: Fetcher): Promise<{ file: Blob; name: string }> => {
    const response = await f("/api/admin/activity");
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new ApiError(body?.error ?? `Request failed (${response.status})`, response.status);
    }
    const disposition = response.headers.get("content-disposition") ?? "";
    const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "Activity.xlsx";
    return { file: await response.blob(), name };
  },

  testScenariosEnabled: (f: Fetcher) =>
    call<{ enabled: boolean }>(f, "/api/admin/test-scenarios").then((r) => r.enabled),
  loadTestScenarios: (f: Fetcher) =>
    call<{ removedProjects: number; projects: number; tasks: number }>(
      f,
      "/api/admin/test-scenarios",
      json({ confirm: "LOAD" }),
    ),

  /** Dev only (db/training-data.ts): replaces ALL work with the training data. */
  trainingDataEnabled: (f: Fetcher) =>
    call<{ enabled: boolean }>(f, "/api/admin/training-data").then((r) => r.enabled),
  loadTrainingData: (f: Fetcher) =>
    call<{ projects: number; tasks: number; linked: number; refused: string[] }>(
      f,
      "/api/admin/training-data",
      json({ confirm: "REPLACE ALL WORK" }),
    ),

  refreshDeltek: (f: Fetcher) =>
    call<{ started: boolean; message: string }>(
      f,
      "/api/admin/deltek/refresh",
      json({}),
    ),

  /**
   * The problem reports, newest first and capped by the server.
   *
   * `more` says the cap was reached, so the panel can point at the export
   * rather than imply it is showing everything.
   */
  problemReports: (f: Fetcher) =>
    call<{ reports: ReportView[]; more: boolean; punch: PunchLabels | null }>(f, "/api/admin/reports"),

  /**
   * The RESOURCE SCHEDULER's problem reports, which Fieldpath fetches from that
   * application over HTTP rather than reading its tables.
   *
   * **Returns a union and does not reject on the scheduler being unavailable.**
   * The API turns every failure into `{ available: false, reason }`, so the
   * panel always has something true to say. A rejection here would collapse
   * into the page-level error and take the other two panels down with it — and
   * worse, a caught-and-defaulted `[]` would say "nobody reported anything",
   * which is the bug this whole route exists to fix.
   */
  schedulerProblemReports: (f: Fetcher) =>
    call<SchedulerReports & { punch?: PunchLabels | null }>(f, "/api/admin/scheduler-reports"),

  /*
   * The punch list (27 Oct 2026). Every call is refused with 403 for anybody
   * not on the setting punchlist.members; the check is on the server.
   */
  /** Whether to offer the page, and whether Jeremy's notes are the reader's to write. */
  punchMe: (f: Fetcher) =>
    call<{ member: boolean; decider: boolean }>(f, "/api/punch/me").catch((e) => {
      // A 403 is the ordinary answer for everybody else, not a failure.
      if (e instanceof ApiError && e.status === 403) return { member: false, decider: false };
      throw e;
    }),

  /** The whole list. Reading it imports the scheduler's new reports first. */
  punchList: (f: Fetcher) => call<PunchListView>(f, "/api/punch"),

  punchHistory: (f: Fetcher, id: string) =>
    call<{ history: PunchHistoryEntry[] }>(f, `/api/punch/${id}/history`).then((r) => r.history),

  createPunchItem: (f: Fetcher, input: NewPunchItemInput) =>
    call<{ item: PunchItem }>(f, "/api/punch", json(input)).then((r) => r.item),

  updatePunchItem: (f: Fetcher, id: string, input: PunchItemInput) =>
    call<{ item: PunchItem }>(f, `/api/punch/${id}`, { ...json(input), method: "PATCH" }).then((r) => r.item),
};

/** Each report's punch-list row ("F94"), by report id. Null for anybody not on the list's members. */
export type PunchLabels = Readonly<Record<string, string>>;

export interface IvpFinding {
  readonly checkId: string;
  readonly area: string;
  readonly title: string;
  readonly status: "pass" | "warn" | "fail" | "unknown";
  readonly summary: string;
  readonly durationMs: number | null;
}

export interface IvpReport {
  readonly ranAt: string;
  readonly ranBy: string | null;
  readonly ageHours: number;
  readonly checksTotal: number;
  readonly passed: number;
  readonly warned: number;
  readonly failed: number;
  readonly unknown: number;
  readonly findings: readonly IvpFinding[];
}

export interface DeltekExecution {
  readonly name: string;
  readonly status: string;
  readonly startedAt: string | null;
}

export interface DeltekEntityRun {
  readonly entity: string;
  readonly outcome: "applied" | "refused" | "failed";
  readonly finishedAt: string | null;
  readonly rowsReturned: number | null;
  readonly rowsUpserted: number | null;
  readonly rowsMissing: number | null;
  readonly detail: string | null;
}

/** How much reference data there IS, as opposed to what a run wrote. */
export interface DeltekCount {
  readonly entity: string;
  /** Live in Deltek and still present in the last extract. */
  readonly active: number;
  /** Every row, including leavers and closed jobs. */
  readonly total: number;
}

export interface DeltekStatus {
  readonly lastRefreshAt: string | null;
  readonly entities: readonly DeltekEntityRun[];
  readonly history: readonly DeltekEntityRun[];
  readonly counts: readonly DeltekCount[];
}
